"use client";
import React, { FC, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslations } from "next-intl";

import { ShowWhen } from "@/components/common";
import { ArrowDownIcon } from "@/components/assets/icons";
import { useOutsideClickDetector } from "@/hooks/useOutsideClickDetector";
import { IDateRangeFilter } from "@/types/parcel-filter.types";

type Props = {
  label: string;
  value: IDateRangeFilter;
  onChange: (value: IDateRangeFilter) => void;
  withBorder?: boolean;
};

const DateRangeFilter: FC<Props> = ({
  label,
  value,
  onChange,
  withBorder = true,
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const t = useTranslations();

  useOutsideClickDetector(ref, () => {
    setIsOpened(false);
  });

  const hasValue = Boolean(value.start || value.end);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "...";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year.slice(2)}`;
  };

  return (
    <div className="flex cursor-pointer items-center gap-2">
      <div
        ref={ref}
        className="relative flex h-14 w-full min-w-[13.62rem] rounded-[33.50px] bg-[#f9f9f9]"
      >
        <div
          onClick={() => setIsOpened(!isOpened)}
          className="z-[99] flex w-full items-center justify-between p-[5px] px-5"
        >
          <p
            className={classNames("truncate text-base font-normal", {
              "text-[#a8a8a8]": !hasValue,
              "text-black": hasValue,
            })}
          >
            {hasValue
              ? `${formatDate(value.start)} — ${formatDate(value.end)}`
              : t(label)}
          </p>
          <div className="flex shrink-0 cursor-pointer items-center">
            {!hasValue && (
              <p className="ml-2 text-base font-normal text-black">
                {t("any")}
              </p>
            )}
            <ArrowDownIcon />
          </div>
        </div>

        <ShowWhen condition={isOpened}>
          <div className="absolute top-0 z-20 w-full rounded-[26px] bg-[#f9f9f9] pb-4 pt-10">
            <div className="mt-4 flex flex-col gap-3 px-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-[#a8a8a8]">
                  {t("date_from")}
                </label>
                <input
                  type="date"
                  value={value.start ?? ""}
                  max={value.end ?? undefined}
                  onChange={(e) =>
                    onChange({ ...value, start: e.target.value || null })
                  }
                  className="w-full rounded-xl bg-white px-3 py-2 text-sm text-black outline-none"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-[#a8a8a8]">{t("date_to")}</label>
                <input
                  type="date"
                  value={value.end ?? ""}
                  min={value.start ?? undefined}
                  onChange={(e) =>
                    onChange({ ...value, end: e.target.value || null })
                  }
                  className="w-full rounded-xl bg-white px-3 py-2 text-sm text-black outline-none"
                />
              </div>
              {hasValue && (
                <button
                  onClick={() => onChange({ start: null, end: null })}
                  className="text-left text-sm text-[#a8a8a8] underline"
                >
                  {t("clear_all")}
                </button>
              )}
            </div>
          </div>
        </ShowWhen>
      </div>

      <ShowWhen condition={withBorder}>
        <div className="w-[25px] rotate-90 border border-[#f1f1f1]" />
      </ShowWhen>
    </div>
  );
};

export default DateRangeFilter;
